import { useState } from 'react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import {
  Store, KeyRound, Loader2, MessagesSquare, Plus, Unplug, Stethoscope, CheckCircle2, AlertTriangle, Download,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Loading, ErrorState, EmptyState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { formatNumber } from '@/lib/utils'
import { i18n, currentLocale } from '@/lib/i18n'
import {
  useTiktokConfig,
  useSaveTiktokConfig,
  useConnectTiktok,
  useTiktokAccounts,
  useDeleteZaloAccount,
  useDiagnoseTiktokChat,
  type TiktokConfig,
  type TiktokChatDiagnosis,
} from '@/hooks/use-integrations'
import { useBackfillTiktokShop, useTiktokBackfillProgress } from '@/hooks/use-tiktok-backfill'
import {
  SelectableTable,
  accountColumns,
  accountSearchText,
  ACCOUNT_FILTERS,
  BulkDisconnectButton,
} from './accounts-table'
import { CopyBox } from './channel-setup-guide'
import { noConnectHint, useAutoAdd } from './channel-catalog'
import { useMyPermissions } from '@/hooks/use-settings'
import { useOnboardingReturn } from '@/hooks/use-onboarding-return'
import { OAUTH_RETURN_TO } from '@/lib/onboarding'

/** Form nhập App Key / App Secret của TikTok Shop (lưu theo org). */
function CredentialsForm({ cfg }: { cfg: TiktokConfig }) {
  const { t } = useTranslation('chanSocial')
  const [appKey, setAppKey] = useState(cfg.appKey)
  const [appSecret, setAppSecret] = useState('')
  const save = useSaveTiktokConfig()

  const handleSave = () => {
    if (!appKey.trim()) {
      toast.error(t('tiktok.cred.appKeyRequired'))
      return
    }
    save.mutate(
      { appKey: appKey.trim(), appSecret: appSecret.trim() || undefined },
      {
        onSuccess: (r) => {
          toast.success(r.message || t('tiktok.cred.saved'))
          setAppSecret('')
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4" />
          {cfg.isConfigured ? t('tiktok.cred.titleUpdate') : t('tiktok.cred.titleSetup')}
          {cfg.isConfigured && (
            <Badge variant="success" className="ml-1">
              {t('shared.configured')}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          <Trans
            t={t}
            i18nKey="tiktok.cred.intro"
            components={{
              b: <strong />,
              link: (
                <a
                  href="https://partner.tiktokshop.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-primary underline"
                />
              ),
            }}
          />
        </p>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">App Key</label>
          <Input
            value={appKey}
            onChange={(e) => setAppKey(e.target.value)}
            placeholder={t('tiktok.cred.appKeyPlaceholder')}
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">App Secret</label>
          <Input
            type="password"
            value={appSecret}
            onChange={(e) => setAppSecret(e.target.value)}
            placeholder={cfg.hasAppSecret ? t('tiktok.cred.secretSaved') : t('tiktok.cred.secretPaste')}
          />
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          <CopyBox label={t('tiktok.cred.redirectUrl')} value={cfg.redirectUri} />
          <CopyBox label={t('tiktok.cred.webhookUrl')} value={cfg.webhookUrl} />
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" />}
            {t('shared.saveConfig')}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

/** Nhãn đọc i18n lúc dùng (không đóng băng ngôn ngữ lúc nạp module). */
function diagStyle(): Record<TiktokChatDiagnosis['status'], { label: string; ok: boolean }> {
  return {
    ok: { label: i18n.t('chanSocial:tiktok.diag.ok'), ok: true },
    scope_missing: { label: i18n.t('chanSocial:tiktok.diag.scope_missing'), ok: false },
    token_expired: { label: i18n.t('chanSocial:tiktok.diag.token_expired'), ok: false },
    not_connected: { label: i18n.t('common:status.notConnected'), ok: false },
    no_creds: { label: i18n.t('chanSocial:tiktok.diag.no_creds'), ok: false },
    bad_credentials: { label: i18n.t('chanSocial:tiktok.diag.bad_credentials'), ok: false },
    error: { label: i18n.t('chanSocial:tiktok.diag.error'), ok: false },
  }
}

/** Kết quả nút "Kiểm tra chat" cho một gian hàng. */
function ChatDiagnosisPanel({ d }: { d: TiktokChatDiagnosis }) {
  const { t } = useTranslation('chanSocial')
  const styles = diagStyle()
  const s = styles[d.status] ?? styles.error
  return (
    <div className="space-y-1.5 text-sm">
      <div className="flex items-center gap-2">
        {s.ok ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-amber-600" />
        )}
        <span className="font-medium">{s.label}</span>
        {d.errorCode ? <span className="text-xs text-muted-foreground">{t('tiktok.diag.code', { code: d.errorCode })}</span> : null}
      </div>
      <p className="text-muted-foreground">{d.message}</p>
      <p className="text-xs text-muted-foreground">
        {t('tiktok.diag.arrived', { count: d.soHoiThoai })}
        {d.tinGanNhat
          ? ` · ${t('tiktok.diag.latest', { time: new Date(d.tinGanNhat).toLocaleString(currentLocale()) })}`
          : ''}
        {d.status === 'ok' && d.soHoiThoai === 0 && d.hasConversations
          ? ` — ${t('tiktok.diag.notArrived')}`
          : ''}
      </p>
    </div>
  )
}

/** Nút "Kéo lịch sử" cho một gian hàng — giai đoạn 2, dùng chung hàng đợi với Zalo cá nhân (xem docs/DONG-BO-TIN-CU-ZALO.md). */
function BackfillShopButton({ accountId }: { accountId: string }) {
  const { t } = useTranslation('chanSocial')
  const backfill = useBackfillTiktokShop()
  const tienDo = useTiktokBackfillProgress(accountId)
  const dangChay = backfill.isPending || tienDo?.status === 'processing'

  return (
    <Button
      variant="ghost"
      size="sm"
      disabled={dangChay}
      title={t('tiktok.backfill.title')}
      onClick={() =>
        backfill.mutate(
          { accountId },
          {
            onSuccess: () => toast.success(t('tiktok.backfill.started')),
            onError: (e) => toast.error(apiError(e)),
          },
        )
      }
    >
      {dangChay ? <Loader2 className="animate-spin" /> : <Download />}
      {dangChay && tienDo?.total ? `${tienDo.current}/${tienDo.total}` : null}
    </Button>
  )
}

/** Dòng tiến độ chi tiết, hiện dưới tên gian hàng khi đang chạy. */
function BackfillProgressLine({ accountId }: { accountId: string }) {
  const { t } = useTranslation('chanSocial')
  const p = useTiktokBackfillProgress(accountId)
  if (p?.status !== 'processing') return null
  return (
    <span className="text-[11px] text-primary">
      {' · '}{t('tiktok.backfill.progress', { current: p.current, total: p.total })}
      {p.threadName ? ` — ${p.threadName}` : ''}
      {p.result ? ` ${t('tiktok.backfill.inserted', { n: formatNumber(p.result.totalInserted ?? 0) })}` : ''}
    </span>
  )
}

/** Danh sách gian hàng TikTok Shop đã kết nối. */
function ConnectedShops() {
  const { t } = useTranslation('chanSocial')
  const { data, isLoading, isError } = useTiktokAccounts()
  const remove = useDeleteZaloAccount()
  const diagnose = useDiagnoseTiktokChat()
  const [chanDoan, setChanDoan] = useState<Record<string, TiktokChatDiagnosis>>({})
  // Backend TikTok Shop đòi `integrations.update` cho cả kéo lịch sử, kiểm tra chat lẫn kết nối; ngắt = delete.
  const { data: perms } = useMyPermissions()
  const canUpdate = !!perms?.has('integrations.update')
  const canDelete = !!perms?.has('integrations.delete')

  const handleDisconnect = (id: string) => {
    remove.mutate(id, {
      onSuccess: () => toast.success(t('tiktok.shops.disconnected')),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <SelectableTable
      rows={data}
      rowKey={(a) => a.id}
      loading={isLoading}
      error={isError}
      filters={ACCOUNT_FILTERS}
      searchText={accountSearchText}
      itemLabel={t('tiktok.shops.itemLabel')}
      empty={
        <EmptyState
          icon={Store}
          title={t('tiktok.shops.emptyTitle')}
          description={canUpdate ? t('tiktok.shops.emptyDescription') : noConnectHint()}
        />
      }
      columns={accountColumns({
        fallbackName: 'TikTok Shop',
        sub: (acc) => (
          <>
            {acc.externalPageId}
            <BackfillProgressLine accountId={acc.id} />
          </>
        ),
        actions:
          canUpdate || canDelete
            ? (acc) => (
                <>
                  {canUpdate && (
                    <>
                      <BackfillShopButton accountId={acc.id} />
                      <Button
                        variant="ghost"
                        size="sm"
                        title={t('tiktok.shops.checkChatTitle')}
                        disabled={diagnose.isPending}
                        onClick={() =>
                          diagnose.mutate(acc.id, {
                            onSuccess: (d) => setChanDoan((s) => ({ ...s, [acc.id]: d })),
                            onError: (e) => toast.error(apiError(e)),
                          })
                        }
                      >
                        <Stethoscope />
                      </Button>
                    </>
                  )}
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDisconnect(acc.id)}
                      disabled={remove.isPending}
                      title={t('common:actions.disconnect')}
                    >
                      <Unplug />
                    </Button>
                  )}
                </>
              )
            : undefined,
      })}
      renderDetail={(acc) => (chanDoan[acc.id] ? <ChatDiagnosisPanel d={chanDoan[acc.id]} /> : null)}
      bulkActions={(selected, clear) => <BulkDisconnectButton selected={selected} onDone={clear} />}
    />
  )
}

/**
 * Tab TikTok Shop — cấu hình khoá ứng dụng, kết nối gian hàng qua OAuth,
 * và quản lý các gian hàng đã kết nối.
 */
export function TiktokShopTab() {
  const { t } = useTranslation('chanSocial')
  // Backend TikTok Shop đòi `integrations.update` cho cả đọc/lưu khoá ứng dụng LẪN connect/start (không phải create).
  const { data: perms, isLoading: permsLoading } = useMyPermissions()
  const canUpdate = !!perms?.has('integrations.update')
  const { data: cfg, isLoading, isError } = useTiktokConfig(canUpdate)
  const connect = useConnectTiktok()

  // Mở từ trang Bắt đầu → callback OAuth đưa về /onboarding (returnTo lưu trong state phía server).
  const onboardingReturn = useOnboardingReturn()
  const handleConnect = () => {
    connect.mutate(onboardingReturn.active ? { returnTo: OAUTH_RETURN_TO } : undefined, {
      onSuccess: (url) => {
        window.location.href = url
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  useAutoAdd(handleConnect, cfg?.isConfigured === true)

  if (isLoading || permsLoading) return <Loading label={t('tiktok.loading')} />
  // Người chỉ có quyền xem: không gọi GET /tiktok-shop/config (403) → vẫn cho xem danh sách gian hàng.
  if (!canUpdate) {
    return (
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">{t('tiktok.shops.heading')}</h3>
        <ConnectedShops />
      </section>
    )
  }
  if (isError || !cfg) return <ErrorState />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('tiktok.intro')}
        </p>
        <Button onClick={handleConnect} disabled={connect.isPending || !cfg.isConfigured} title={cfg.isConfigured ? '' : t('tiktok.configFirst')}>
          {connect.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
          {t('tiktok.connect')}
        </Button>
      </div>

      {/* Chat người mua dùng Customer Service API — scope custom, TikTok duyệt riêng.
          docs/KET-NOI-UU-TIEN-TIKTOK-SHOPEE-TELEGRAM.md */}
      <div className="flex gap-3 rounded-lg border border-primary/20 bg-primary/5 p-4 text-sm">
        <MessagesSquare className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="space-y-1.5">
          <p className="font-medium">{t('tiktok.chat.title')}</p>
          <p className="text-muted-foreground">
            <Trans
              t={t}
              i18nKey="tiktok.chat.requirement"
              components={{ b: <strong />, code: <code className="text-xs" /> }}
            />
          </p>
          <p className="text-muted-foreground">
            <Trans
              t={t}
              i18nKey="tiktok.chat.check"
              components={{
                icon: <Stethoscope className="inline h-3.5 w-3.5 align-text-bottom" />,
                em: <em />,
              }}
            />
          </p>
          <p className="text-xs text-muted-foreground">
            {t('tiktok.chat.limits')}
          </p>
        </div>
      </div>

      <CredentialsForm cfg={cfg} />

      <section className="space-y-3">
        <h3 className="text-sm font-semibold">{t('tiktok.shops.heading')}</h3>
        <ConnectedShops />
      </section>
    </div>
  )
}
